/**
 * Composer (conversation view)
 *
 * Three-mode composer docked at the bottom of the conversation card (the parent
 * provides the card chrome; this renders the border-t bar):
 *   - Text  -> POST /contacts/:id/messages (gated manual SMS; quiet-hours-exempt;
 *              opt-out + consent-revoke still hard-block server-side). In test
 *              mode the API is fail-closed without CRM_SMS_OVERRIDE_TO and returns
 *              422 + a hint, surfaced here.
 *   - Email -> POST /contacts/:id/emails (Part 5: gated manual email — no_email /
 *              consent_revoked / suppressed block server-side; fail-closed in
 *              test mode without CRM_EMAIL_OVERRIDE_TO). Adds a subject input.
 *   - Note  -> POST /contacts/:id/notes (internal note). In Note mode the box is
 *              the mention input (mentions/MentionInput): "@" names a teammate,
 *              and the teammates whose "@Name" is still in the text at Post go
 *              as `mentions` (mentions/api createNote; none = exactly today's
 *              { body }). A `mentions: { failed: true }` answer means the note
 *              saved but nobody could be told, and the line under the box says
 *              so ("Saved; we could not tell Dana. ...").
 *
 * When a study is in context (the contact's considering study), Text/Email mode
 * also offer: a "Templates" dropdown (per-study + global reply snippets — pick
 * one to pre-fill the message), an "Attach" picker for the study's files (the
 * ICF/protocol — emailed as a real attachment, texted as a secure link), and a
 * "Save as template" shortcut so recruiters can stash their own quick replies.
 *
 * Messaging-app ergonomics: small Text|Email|Note pills, an auto-growing
 * textarea, a compact icon send button (44px touch target), inline char counter,
 * Cmd/Ctrl+Enter to send. On success it clears the box and calls onSent().
 *
 * The Note pill carries one extra gesture (owner ask 2026-08-07): DOUBLE-click
 * it and the thread above consolidates to notes only — the team's running
 * record of the lead, without scrolling past the texts. A hover popover on the
 * pill advertises it (nobody discovers an undocumented double-click). Both are
 * inert unless the host passes `onToggleNotesFilter`.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Send, StickyNote, MessageSquare, Mail, FileText, Paperclip, X, Bookmark, Braces, Eye, AlertTriangle, Clock, Bot } from 'lucide-react';
import api from '../../services/api';
import { MessageTemplate, StudyDocument } from '../types';
import TemplatePickerModal from './TemplatePickerModal';
import VariablePicker from './VariablePicker';
import { findUnknownMergeFields, insertAtCursor, MERGE_FIELDS } from '../mergeFields';
import { usePromptDialog, Z } from './ui';
import { pickComposerWarning } from './lead/composerWarning';
import MentionInput, { namedAtPost } from '../mentions/MentionInput';
import { createNote } from '../mentions/api';
import { notifyFailedWords } from '../mentions/mentionLogic';
import type { MentionableUser } from '../mentions/types';

const SMS_SOFT_LIMIT = 320; // ~2 segments; backend hard cap is 1600
const EMAIL_BODY_LIMIT = 10000; // backend hard cap
const TA_MAX_HEIGHT = 132; // ~5 rows of text-sm

// Toolbar buttons (Templates / Attach file). 44px touch target on phones;
// desktop collapses to the natural pill height so the control strip — mode
// pills, toolbar, counter — fits on a single line above the message box.
const toolBtnCls =
  'inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700 sm:min-h-0';

// datetime-local values are LOCAL wall-clock (no timezone). Format a Date into
// that shape, and default the picker to tomorrow 9am (always future + in hours).
function toLocalInputValue(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}
function defaultScheduleValue(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return toLocalInputValue(d);
}

interface Props {
  contactId: string;
  /** The contact's considering/primary study — enables templates + file attach. */
  studyId?: string | null;
  /** Recipient name for the templates picker's "To:" line. */
  contactName?: string;
  smsOptOut: boolean;
  hasPhone: boolean;
  hasEmail: boolean;
  onSent: () => void;
  /** Called after a message is queued for a future send (refreshes the bar). */
  onScheduled?: () => void;
  /** One-shot draft pushed in from a quick action (e.g. booking link). */
  prefill?: { text: string; mode?: 'sms' | 'email' | 'note' } | null;
  onPrefillConsumed?: () => void;
  /** C-13: the texting agent has an ACTIVE conversation on this thread — a
   * manual text/email send auto-pauses it (§6.5 human-touch). Fed by
   * AgentPanel's existing fetch via ContactDetail; no probe of its own. */
  agentActive?: boolean;
  /** A copilot draft is sitting in the dock right above this composer awaiting
   * approval. Sharpens the C-13 warning: sending manually here does not just
   * pause the agent, it strands a written reply the recruiter may not have
   * scrolled to. */
  agentDraftPending?: boolean;
  /** Owner ask 2026-08-07: double-clicking the Note pill consolidates the
   *  recruiters' notes — it flips the thread above into a notes-only view.
   *  Absent = no notes-only view here (call mode), and the pill renders exactly
   *  as before, hover hint included. */
  onToggleNotesFilter?: () => void;
  /** Is that notes-only view currently on? Only lights the pill's hint copy —
   *  the filter state itself lives in the host beside the thread. */
  notesFilterActive?: boolean;
}

function friendlyReason(reason?: string): string {
  switch (reason) {
    case 'opted_out': return 'contact has opted out of SMS';
    case 'consent_revoked': return 'consent was revoked';
    case 'quiet_hours': return 'outside allowed messaging hours';
    case 'no_phone': return 'contact has no phone number';
    case 'no_email': return 'contact has no email address';
    case 'suppressed': return 'this email address previously bounced or was reported as spam';
    case 'empty_subject': return 'subject is empty';
    case 'subject_too_long': return 'subject is too long';
    case 'empty_body': return 'message is empty';
    case 'too_long': return 'message is too long';
    case 'time_in_past': return 'the chosen time has already passed';
    case 'too_far': return 'that is more than 90 days out';
    case 'invalid_time': return 'the chosen time is invalid';
    case 'not_editable': return 'this message has already started sending';
    case 'not_cancelable': return 'this message has already started sending';
    default: return reason || 'blocked';
  }
}

export default function Composer({ contactId, studyId, contactName, smsOptOut, hasPhone, hasEmail, onSent, onScheduled, prefill, onPrefillConsumed, agentActive = false, agentDraftPending = false, onToggleNotesFilter, notesFilterActive = false }: Props) {
  const prompt = usePromptDialog();
  const [mode, setMode] = useState<'sms' | 'email' | 'note'>('sms');
  const [body, setBody] = useState('');
  const [subject, setSubject] = useState('');
  const [sending, setSending] = useState(false);
  // Schedule-for-later popover state.
  const [scheduling, setScheduling] = useState(false);
  const [schedAt, setSchedAt] = useState('');
  const [schedBusy, setSchedBusy] = useState(false);
  // `warn`: done, with a caveat (the note saved but its mentions could not be sent).
  const [note, setNote] = useState<{ ok: boolean; text: string; warn?: boolean } | null>(null);
  // The teammates picked with "@" in Note mode (MentionInput). Only those whose "@Name" is
  // still in the text at Post are sent; cleared with the box.
  const [picked, setPicked] = useState<MentionableUser[]>([]);
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  const subjectRef = useRef<HTMLInputElement | null>(null);
  // Insert a clicked merge-field token into whichever field was last focused.
  const [activeField, setActiveField] = useState<'body' | 'subject'>('body');

  // Study-scoped extras (templates + file attach), only when a study is in context.
  const [templates, setTemplates] = useState<MessageTemplate[]>([]);
  const [docs, setDocs] = useState<StudyDocument[]>([]);
  const [docsLoaded, setDocsLoaded] = useState(false);
  const [docsLoading, setDocsLoading] = useState(false);
  const [attachedIds, setAttachedIds] = useState<string[]>([]);
  const [panel, setPanel] = useState<null | 'attach'>(null);
  const [showTemplates, setShowTemplates] = useState(false);
  // Phones hide the merge-field chip row by default (two rows of chips on a
  // 375px screen buries the message box) — this toggle brings it back.
  const [showMobileFields, setShowMobileFields] = useState(false);

  // Live send preview: the SAME merge-field render the server runs at send time
  // ({{study_title}} → the real study name), so the recruiter sees exactly what
  // the contact will receive instead of the raw token.
  const [preview, setPreview] = useState<{ subject?: string; body: string } | null>(null);
  const [previewing, setPreviewing] = useState(false);

  const smsDisabled = smsOptOut || !hasPhone;
  const emailDisabled = !hasEmail;
  const effectiveMode =
    (mode === 'sms' && smsDisabled) || (mode === 'email' && emailDisabled) ? 'note' : mode;
  const canSendFiles = effectiveMode === 'sms' || effectiveMode === 'email';

  // Load templates available here: the contact's study templates + network-wide
  // global templates (the backend returns globals even with no study in context).
  useEffect(() => {
    setTemplates([]);
    setDocs([]);
    setDocsLoaded(false);
    setDocsLoading(false);
    setAttachedIds([]);
    setPanel(null);
    setShowTemplates(false);
    let alive = true;
    api.get('/api/crm/templates', { params: studyId ? { study_id: studyId } : {} })
      .then((r) => { if (alive) setTemplates(r.data?.templates || []); })
      .catch(() => { /* templates are best-effort */ });
    return () => { alive = false; };
  }, [studyId]);

  // Lazy-load the study's files only when the attach picker is first opened.
  // Only the consent form (ICF) is attachable here — recruiters send the ICF to
  // patients, never the protocol/amendments. Filter to doc_type 'icf' so the
  // protocol can never be picked (and attachedIds can only ever hold ICF ids).
  const loadDocs = useCallback(() => {
    if (docsLoaded || docsLoading || !studyId) return;
    setDocsLoading(true);
    api.get(`/api/crm/studies/${studyId}/documents`)
      .then((r) => setDocs((r.data?.documents || []).filter((d: StudyDocument) => d.doc_type === 'icf')))
      .catch(() => { /* picker just shows empty */ })
      .finally(() => { setDocsLoaded(true); setDocsLoading(false); });
  }, [docsLoaded, docsLoading, studyId]);

  // Templates available for the current channel (channel match or 'any').
  const channelTemplates = templates.filter((t) => t.channel === 'any' || t.channel === effectiveMode);
  const attachedDocs = docs.filter((d) => attachedIds.includes(d.id));

  // A draft has merge fields to resolve when it contains {{...}} (notes never send).
  const hasMergeTokens =
    effectiveMode !== 'note' && (body.includes('{{') || (effectiveMode === 'email' && subject.includes('{{')));

  // Token-shaped mistypes ({{study}}) that would send blank — warn (non-blocking).
  const unknownDraftTokens = effectiveMode === 'note' ? [] : [
    ...new Set([
      ...findUnknownMergeFields(body),
      ...(effectiveMode === 'email' ? findUnknownMergeFields(subject) : []),
    ]),
  ];

  // The composer shows AT MOST ONE standing warning — pickComposerWarning
  // ranks them (draft_pending > channel_blocked > unknown_tokens) so a blocked
  // channel and a pending draft can't stack three lines under the box.
  const warning = pickComposerWarning({
    agentActive,
    agentDraftPending,
    mode,
    effectiveMode,
    smsDisabled,
    emailDisabled,
    unknownTokenCount: unknownDraftTokens.length,
  });

  // The always-on "sending manually pauses the AI agent" line is retired
  // by folding it into the placeholder — read exactly when you start typing,
  // and it costs the thread no vertical space.
  const agentPauseInPlaceholder = agentActive && effectiveMode !== 'note';
  const bodyPlaceholder =
    effectiveMode === 'sms'
      ? (agentPauseInPlaceholder ? 'Type a text — sending manually pauses the AI agent…' : 'Type a text message…')
      : effectiveMode === 'email'
        ? (agentPauseInPlaceholder ? 'Type an email — sending manually pauses the AI agent…' : 'Type an email…')
        : 'Add an internal note… type @ to mention a teammate';

  const insertToken = (token: string) => {
    if (activeField === 'subject' && effectiveMode === 'email') {
      insertAtCursor(subjectRef.current, subject, setSubject, token);
    } else {
      insertAtCursor(taRef.current, body, setBody, token);
      requestAnimationFrame(autoGrow);
    }
  };

  // Auto-grow the textarea from 1 to ~5 rows; reset after a successful send.
  const autoGrow = () => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    ta.style.height = `${Math.min(ta.scrollHeight, TA_MAX_HEIGHT)}px`;
  };
  const resetHeight = () => {
    const ta = taRef.current;
    if (ta) ta.style.height = 'auto';
  };

  const tooLong =
    (effectiveMode === 'sms' && body.length > 1600) ||
    (effectiveMode === 'email' && body.length > EMAIL_BODY_LIMIT);
  const missingSubject = effectiveMode === 'email' && !subject.trim();
  // A file-only text (secure link, no typed body) is allowed; email/note need text.
  const hasText = !!body.trim();
  const sendable =
    effectiveMode === 'sms' ? (hasText || attachedIds.length > 0)
      : hasText;
  const disableSend = sending || !sendable || tooLong || missingSubject;

  const applyTemplate = (t: MessageTemplate) => {
    setBody(t.body);
    setPicked([]);
    // Set the subject deterministically in email mode: a subject-less template
    // (e.g. an 'any'-channel snippet) must CLEAR the field, never inherit the
    // subject left behind by a previously-applied template.
    if (effectiveMode === 'email') setSubject(t.subject || '');
    setShowTemplates(false);
    setNote(null);
    requestAnimationFrame(() => { autoGrow(); taRef.current?.focus(); });
  };

  // One-shot draft pushed in from a quick action (booking link): mirror
  // applyTemplate — set mode + body, clear status, grow + focus the textarea.
  useEffect(() => {
    if (!prefill) return;
    if (prefill.mode) setMode(prefill.mode);
    setBody(prefill.text);
    setPicked([]);
    setNote(null);
    requestAnimationFrame(() => { autoGrow(); taRef.current?.focus(); });
    onPrefillConsumed?.();
  }, [prefill]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced live preview: render the draft server-side (same path as send)
  // whenever it carries merge tokens. No tokens → no preview (it's already WYSIWYG).
  useEffect(() => {
    if (!hasMergeTokens) { setPreview(null); setPreviewing(false); return; }
    let alive = true;
    setPreviewing(true);
    const t = setTimeout(() => {
      api.post(`/api/crm/contacts/${contactId}/message-preview`, {
        body,
        ...(effectiveMode === 'email' ? { subject } : {}),
      })
        .then((r) => { if (alive) setPreview({ subject: r.data?.subject, body: r.data?.body ?? '' }); })
        .catch(() => { if (alive) setPreview(null); })
        .finally(() => { if (alive) setPreviewing(false); });
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [body, subject, effectiveMode, contactId, hasMergeTokens]);

  const toggleAttach = (id: string) => {
    setAttachedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const saveAsTemplate = async () => {
    if (!studyId || !hasText) return;
    const title = await prompt({
      title: 'Save as template',
      message: 'Save this as a reusable template.',
      label: 'Short label',
      confirmLabel: 'Save',
    });
    if (!title || !title.trim()) return;
    try {
      await api.post(`/api/crm/studies/${studyId}/templates`, {
        channel: effectiveMode === 'email' ? 'email' : 'sms',
        title: title.trim(),
        subject: effectiveMode === 'email' ? subject.trim() : '',
        body: body.trim(),
      });
      const r = await api.get('/api/crm/templates', { params: { study_id: studyId } });
      setTemplates(r.data?.templates || []);
      setNote({ ok: true, text: 'Saved as a template.' });
    } catch (e: any) {
      setNote({ ok: false, text: e?.response?.data?.error || 'Could not save the template.' });
    }
  };

  const submit = async () => {
    const text = body.trim();
    // mirrors the send button's disabled state so Cmd/Ctrl+Enter can't fire a
    // doomed send.
    if (!sendable || sending || tooLong || missingSubject) return;
    setSending(true);
    setNote(null);
    const docIds = canSendFiles ? attachedIds : [];
    try {
      if (effectiveMode === 'sms') {
        const res = await api.post(`/api/crm/contacts/${contactId}/messages`, {
          body: text,
          ...(docIds.length ? { study_document_ids: docIds } : {}),
        });
        setBody(''); setPicked([]); setAttachedIds([]); resetHeight();
        setNote({ ok: true, text: res.data?.overridden ? 'Sent (test override active — see timeline).' : 'Message sent.' });
        onSent();
      } else if (effectiveMode === 'email') {
        const res = await api.post(`/api/crm/contacts/${contactId}/emails`, {
          subject: subject.trim(),
          body: text,
          ...(docIds.length ? { study_document_ids: docIds } : {}),
        });
        setBody(''); setPicked([]); setSubject(''); setAttachedIds([]); resetHeight();
        setNote({ ok: true, text: res.data?.overridden ? 'Email sent (test override active — see timeline).' : 'Email sent.' });
        onSent();
      } else {
        // The teammates whose "@Name" is still in the text (a name edited out tells
        // nobody); with none the request is exactly today's { body }.
        const named = namedAtPost(text, picked);
        const res = await createNote(contactId, { body: text, mentions: named.ids });
        setBody(''); setPicked([]); resetHeight();
        setNote(res.mentions.failed
          ? { ok: true, warn: true, text: notifyFailedWords(named.users) }
          : { ok: true, text: 'Note added.' });
        onSent();
      }
    } catch (e: any) {
      const status = e?.response?.status;
      const d = e?.response?.data || {};
      let text2 = d.detail || d.error || e.message;
      if (status === 409 && d.reason) text2 = `Not sent: ${friendlyReason(d.reason)}.`;
      else if (status === 400 && d.reason) text2 = `Not sent: ${friendlyReason(d.reason)}.`;
      else if (status === 422) text2 = `${d.error || 'Not sent'}${d.hint ? ` — ${d.hint}` : ''}`;
      setNote({ ok: false, text: text2 });
    } finally {
      setSending(false);
    }
  };

  // ----- Schedule for later -----
  const schedDate = schedAt ? new Date(schedAt) : null;
  const schedInvalid = !schedDate || Number.isNaN(schedDate.getTime());
  const schedInPast = !schedInvalid && schedDate!.getTime() <= Date.now();
  // SMS respects quiet hours (9pm–8am); email has no time-of-day rule. This is a
  // local-time guard for immediate feedback — the server re-checks in the
  // contact's timezone and auto-defers a send that still lands in quiet hours.
  const schedInQuietHours = !schedInvalid && (schedDate!.getHours() >= 21 || schedDate!.getHours() < 8);
  const schedBlocked = schedInvalid || schedInPast || (effectiveMode === 'sms' && schedInQuietHours);

  const openScheduler = () => {
    if (!sendable) return;
    setSchedAt((prev) => prev || defaultScheduleValue());
    setScheduling((s) => !s);
    setNote(null);
  };

  const submitSchedule = async () => {
    if (!sendable || sending || schedBusy || schedBlocked || tooLong || missingSubject) return;
    const when = new Date(schedAt);
    setSchedBusy(true);
    setNote(null);
    const docIds = canSendFiles ? attachedIds : [];
    try {
      await api.post(`/api/crm/contacts/${contactId}/scheduled-messages`, {
        channel: effectiveMode === 'email' ? 'email' : 'sms',
        body: body.trim(),
        ...(effectiveMode === 'email' ? { subject: subject.trim() } : {}),
        send_at: when.toISOString(),
        ...(docIds.length ? { study_document_ids: docIds } : {}),
        ...(studyId ? { study_id: studyId } : {}),
      });
      setBody(''); setPicked([]); setSubject(''); setAttachedIds([]); resetHeight();
      setScheduling(false);
      setNote({
        ok: true,
        text: `Scheduled for ${when.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.`,
      });
      onScheduled?.();
    } catch (e: any) {
      const d = e?.response?.data || {};
      setNote({ ok: false, text: d.reason ? `Not scheduled: ${friendlyReason(d.reason)}.` : (d.detail || d.error || e.message) });
    } finally {
      setSchedBusy(false);
    }
  };

  const modeBtnCls = (active: boolean, disabled = false) =>
    `inline-flex min-h-[44px] items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0 ${
      active
        ? 'bg-primary-600 text-white'
        : `text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200${disabled ? ' disabled:cursor-not-allowed disabled:opacity-40' : ''}`
    }`;

  return (
    <div className="border-t border-gray-200 p-3 dark:border-gray-700">
      {/* Mode pills + Templates/Attach/Variables toolbar + inline counter/hint.
          Desktop keeps the whole control strip on ONE line so the composer
          doesn't stack three toolbars of white space above the message box;
          phones drop the toolbar onto its own line under the pills (order-*
          swaps the counter and the toolbar between the two layouts). */}
      <div className="mb-2 flex flex-wrap items-center gap-x-1.5 gap-y-2">
        <div className="order-1 inline-flex shrink-0 rounded-lg bg-gray-100 p-0.5 dark:bg-gray-700/60">
          <button onClick={() => setMode('sms')} disabled={smsDisabled} className={modeBtnCls(effectiveMode === 'sms', true)}
            title={smsDisabled ? (smsOptOut ? 'Contact opted out of SMS' : 'No phone number on file') : 'Send a text'}>
            <MessageSquare className="h-3.5 w-3.5" /> Text
          </button>
          <button onClick={() => setMode('email')} disabled={emailDisabled} className={modeBtnCls(effectiveMode === 'email', true)}
            title={emailDisabled ? 'No email address on file' : 'Send an email'}>
            <Mail className="h-3.5 w-3.5" /> Email
          </button>
          {/* Note pill. Single click = write a note (unchanged). DOUBLE click =
              consolidate: the thread above flips to notes only, so a recruiter
              can read everything the team has written about this lead without
              scrolling past months of texts. The hover popup is how anyone
              finds that out — a `title` tooltip was too easy to miss, so it's a
              real popover. Hover/focus only, and suppressed below sm: (a phone
              has no hover, and a sticky group-hover panel there would just sit
              over the pills). */}
          <span className="group relative inline-flex">
            <button
              onClick={() => setMode('note')}
              onDoubleClick={onToggleNotesFilter}
              aria-describedby={onToggleNotesFilter ? 'composer-note-hint' : undefined}
              className={modeBtnCls(effectiveMode === 'note')}
            >
              <StickyNote className="h-3.5 w-3.5" /> Note
            </button>
            {onToggleNotesFilter && (
              <span
                id="composer-note-hint"
                role="tooltip"
                className={`pointer-events-none absolute bottom-full left-0 mb-1.5 hidden w-max max-w-[15rem] rounded-lg bg-gray-900 px-2.5 py-1.5 text-[11px] font-medium leading-snug text-white shadow-lg dark:bg-gray-700 sm:group-focus-within:block sm:group-hover:block ${Z.menu}`}
              >
                {notesFilterActive ? (
                  <>Double-click to show the whole conversation again</>
                ) : (
                  <>Double-click to see only the notes on this lead</>
                )}
              </span>
            )}
          </span>
        </div>

        {/* Templates / Attach / Variables (Text/Email only) */}
        {canSendFiles && (
          <div className="order-3 flex w-full flex-wrap items-center gap-1.5 sm:order-2 sm:w-auto">
            {channelTemplates.length > 0 && (
              <button
                type="button"
                onClick={() => setShowTemplates(true)}
                className={toolBtnCls}
              >
                <FileText className="h-3.5 w-3.5" /> Templates
              </button>
            )}
            {studyId && (
              <button
                type="button"
                onClick={() => { setPanel((p) => (p === 'attach' ? null : 'attach')); loadDocs(); }}
                className={toolBtnCls}
                title={effectiveMode === 'email' ? 'Attach study files (sent as attachments)' : 'Attach study files (texted as a secure link)'}
              >
                <Paperclip className="h-3.5 w-3.5" /> Attach file{attachedIds.length ? ` · ${attachedIds.length}` : ''}
              </button>
            )}
            {/* Phone-only: reveal the merge-field picker on demand (hidden by
                default there — the desktop row always shows it). */}
            <button
              type="button"
              onClick={() => setShowMobileFields((s) => !s)}
              aria-pressed={showMobileFields}
              className={`inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs font-medium transition-colors duration-150 sm:hidden ${
                showMobileFields
                  ? 'border-primary-600 text-primary-700 dark:text-primary-400'
                  : 'border-gray-300 text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
              title="Insert a merge field ({{first_name}}, {{study_title}}, …)"
            >
              <Braces className="h-3.5 w-3.5" /> Fields
            </button>
            <div className={showMobileFields ? 'inline-flex' : 'hidden sm:inline-flex'}>
              <VariablePicker items={MERGE_FIELDS} onInsert={insertToken} />
            </div>
            {/* Desktop-only workflow nicety — phones keep the toolbar to the
                essentials (templates, files, fields). */}
            {studyId && hasText && (
              <button
                type="button"
                onClick={saveAsTemplate}
                className="hidden min-h-[44px] items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium text-gray-500 transition-colors duration-150 hover:text-primary-700 dark:text-gray-400 dark:hover:text-primary-300 sm:inline-flex sm:min-h-0"
                title="Save the current message as a reusable template for this study"
              >
                <Bookmark className="h-3.5 w-3.5" /> Save as template
              </button>
            )}
          </div>
        )}

        <div className="order-2 ml-auto shrink-0 text-[11px] text-gray-400 sm:order-3">
          {effectiveMode === 'sms' ? (
            <>
              <span className={tooLong ? 'text-red-500' : body.length > SMS_SOFT_LIMIT ? 'text-amber-500' : ''}>
                {body.length} chars{body.length > SMS_SOFT_LIMIT ? ' · multiple segments' : ''}
              </span>
              <span className="hidden lg:inline"> · ⌘/Ctrl+Enter to send</span>
            </>
          ) : effectiveMode === 'email' ? (
            <>
              {tooLong && <span className="text-red-500">{body.length} chars · too long</span>}
              <span className="hidden lg:inline">{tooLong ? ' · ' : ''}⌘/Ctrl + Enter to send</span>
            </>
          ) : (
            <span className="hidden lg:inline">⌘/Ctrl + Enter to save</span>
          )}
        </div>
      </div>

      {(warning === 'channel_blocked') && smsDisabled && mode === 'sms' && (
        <p className="mb-2 text-xs text-amber-600 dark:text-amber-400">
          {smsOptOut ? 'Contact has opted out of SMS' : 'No phone number on file'} — you can still add an internal note.
        </p>
      )}
      {(warning === 'channel_blocked') && emailDisabled && mode === 'email' && (
        <p className="mb-2 text-xs text-amber-600 dark:text-amber-400">
          No email address on file — you can still add an internal note.
        </p>
      )}

      {/* Templates picker (popup) */}
      {showTemplates && (
        <TemplatePickerModal
          templates={templates}
          channel={effectiveMode === 'email' ? 'email' : 'sms'}
          contactName={contactName}
          onPick={applyTemplate}
          onClose={() => setShowTemplates(false)}
        />
      )}

      {/* Attach panel */}
      {panel === 'attach' && (
        <div className="mb-2 max-h-44 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700">
          {docs.length === 0 ? (
            <p className="px-3 py-3 text-xs text-gray-500 dark:text-gray-400">
              {docsLoaded ? 'No consent form (ICF) uploaded for this study yet.' : 'Loading…'}
            </p>
          ) : (
            docs.map((d) => (
              <label key={d.id} className="flex cursor-pointer items-center gap-2 border-b border-gray-100 px-3 py-2 last:border-0 hover:bg-gray-50 dark:border-gray-700/60 dark:hover:bg-gray-700/40">
                <input type="checkbox" checked={attachedIds.includes(d.id)} onChange={() => toggleAttach(d.id)} className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                <FileText className="h-3.5 w-3.5 shrink-0 text-gray-400" />
                <span className="min-w-0 flex-1 truncate text-xs text-gray-700 dark:text-gray-200">{d.filename}</span>
                <span className="shrink-0 text-[10px] uppercase tracking-wide text-gray-400">{d.doc_type}</span>
              </label>
            ))
          )}
        </div>
      )}

      {/* Attached-file chips */}
      {attachedDocs.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          {attachedDocs.map((d) => (
            <span key={d.id} className="inline-flex max-w-full items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 text-xs text-primary-700 dark:bg-primary-900/30 dark:text-primary-300">
              <Paperclip className="h-3 w-3 flex-shrink-0" /> <span className="min-w-0 truncate" title={d.filename}>{d.filename}</span>
              <button type="button" onClick={() => toggleAttach(d.id)} aria-label={`Remove ${d.filename}`} className="ml-0.5 flex-shrink-0 rounded-full hover:text-primary-900 dark:hover:text-primary-100">
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          {effectiveMode === 'sms' && (
            <span className="text-[11px] text-gray-400">texted as a secure link</span>
          )}
        </div>
      )}

      {effectiveMode === 'email' && (
        <input
          ref={subjectRef}
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          onFocus={() => setActiveField('subject')}
          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit(); }}
          maxLength={200}
          placeholder="Subject…"
          aria-label="Email subject"
          className="mb-2 min-h-[44px] w-full rounded-xl border border-gray-300 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 transition-colors duration-150 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400 sm:min-h-0"
        />
      )}

      {/* Schedule-for-later popover (Text/Email only) */}
      {scheduling && effectiveMode !== 'note' && (
        <div className="mb-2 rounded-lg border border-primary-200 bg-primary-50/60 p-3 dark:border-primary-900/50 dark:bg-primary-900/15">
          <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-primary-700 dark:text-primary-300">
            <Clock className="h-3 w-3" /> Schedule this {effectiveMode === 'email' ? 'email' : 'text'}
          </div>
          <input
            type="datetime-local"
            value={schedAt}
            min={toLocalInputValue(new Date())}
            onChange={(e) => setSchedAt(e.target.value)}
            aria-label="Send date and time"
            className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
          />
          {effectiveMode === 'sms' && schedInQuietHours && !schedInvalid && (
            <p className="mt-1.5 flex items-start gap-1 text-xs text-amber-600 dark:text-amber-400">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>That&apos;s inside quiet hours (9pm–8am). Pick a time between 8am and 9pm — texts can&apos;t go out overnight.</span>
            </p>
          )}
          {schedInPast && !schedInvalid && (
            <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400">Pick a time in the future.</p>
          )}
          <div className="mt-2 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setScheduling(false)}
              className="rounded-lg px-2.5 py-1 text-xs font-medium text-gray-500 transition-colors duration-150 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submitSchedule}
              disabled={disableSend || schedBusy || schedBlocked}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-xs font-semibold text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {schedBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Clock className="h-3.5 w-3.5" />} Schedule
            </button>
          </div>
        </div>
      )}

      <div className="flex items-end gap-2">
        {/* The message box. In Note mode "@" names a teammate: the list opens
            where it fits, above the box as a rule (it is docked at the bottom
            of the card), and Esc closes only the list. Text and Email keep a
            plain box. The wrapper is the flex item now, so the box itself is
            block + full width. */}
        <MentionInput
          ref={taRef}
          value={body}
          onChange={(next) => {
            setBody(next);
            autoGrow();
            // A pick replaces "@..." before the box shows it: measure again once it does.
            if (taRef.current && taRef.current.value !== next) requestAnimationFrame(autoGrow);
          }}
          picked={picked}
          onPickedChange={setPicked}
          enabled={effectiveMode === 'note'}
          onFocus={() => setActiveField('body')}
          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit(); }}
          rows={1}
          placeholder={bodyPlaceholder}
          wrapperClassName="min-w-0 flex-1"
          className="block max-h-[132px] min-h-[44px] w-full resize-none overflow-y-auto rounded-xl border border-gray-300 bg-white px-3.5 py-2.5 text-sm text-gray-900 placeholder-gray-400 transition-colors duration-150 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-400"
        />
        {effectiveMode !== 'note' && (
          <button
            type="button"
            onClick={openScheduler}
            disabled={disableSend}
            aria-label="Schedule this message for later"
            title="Schedule for later"
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 ${
              scheduling
                ? 'border-primary-600 bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300'
                : 'border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-primary-700 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-700'
            }`}
          >
            <Clock className="h-4 w-4" />
          </button>
        )}
        <button
          onClick={submit}
          disabled={disableSend}
          aria-label={effectiveMode === 'sms' ? 'Send text message' : effectiveMode === 'email' ? 'Send email' : 'Add note'}
          title={
            effectiveMode === 'sms' ? 'Send text (⌘/Ctrl + Enter)'
              : effectiveMode === 'email' ? (missingSubject ? 'Add a subject to send' : 'Send email (⌘/Ctrl + Enter)')
              : 'Add note (⌘/Ctrl + Enter)'
          }
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-600 text-white transition-colors duration-150 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </div>

      {/* C-13: the coexistence warning the handbook promises — a manual
          text/email to an agent-active thread auto-pauses the agent (§6.5
          human-touch). Notes are internal and don't pause anything. Only the
          draft-pending half survives as a line (the generic sentence is the
          placeholder now): stranding a written reply is a consequence a
          placeholder cannot carry. */}
      {(warning === 'draft_pending') && agentActive && effectiveMode !== 'note' && (
        <p className="mt-1.5 flex items-start gap-1 text-xs text-amber-600 dark:text-amber-400">
          <Bot className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {agentDraftPending
              ? 'The agent has a reply waiting for approval just above. Sending manually pauses the agent and abandons that draft.'
              : 'Sending manually pauses the AI agent on this thread.'}
          </span>
        </p>
      )}

      {/* Live preview — what the contact actually receives once merge fields
          (e.g. {{study_title}}) are filled in at send. Shown only while the
          draft still has tokens to resolve. */}
      {hasMergeTokens && (
        <div className="mt-2 rounded-lg border border-primary-200 bg-primary-50/60 px-3 py-2 dark:border-primary-900/50 dark:bg-primary-900/15">
          <div className="mb-1 flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-primary-700 dark:text-primary-300">
            {previewing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
            Preview · what {contactName || 'the contact'} will see
          </div>
          {effectiveMode === 'email' && preview?.subject !== undefined && (
            <p className="mb-0.5 text-xs font-semibold text-gray-800 dark:text-gray-100">
              {preview.subject || <span className="font-normal italic text-gray-400">(no subject)</span>}
            </p>
          )}
          <p className="whitespace-pre-wrap break-words text-xs text-gray-700 dark:text-gray-200">
            {preview ? preview.body : <span className="italic text-gray-400">Rendering…</span>}
          </p>
        </div>
      )}

      {(warning === 'unknown_tokens') && unknownDraftTokens.length > 0 && (
        <p className="mt-1.5 flex items-start gap-1 text-xs text-amber-600 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            {unknownDraftTokens.map((t) => `{{${t}}}`).join(', ')}{' '}
            {unknownDraftTokens.length > 1 ? 'are not recognized fields' : 'is not a recognized field'} — it will send blank. Use Insert field.
          </span>
        </p>
      )}

      {note && (
        <p className={`mt-1.5 text-xs ${note.warn ? 'text-amber-600 dark:text-amber-400' : note.ok ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
          {note.text}
        </p>
      )}
    </div>
  );
}
